import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  FormHelperText,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  useGetCopyInboundTemplateListQuery,
  useGetCopyOutboundTemplateListQuery,
  useGetWorkflowsCopyQuery,
} from "../../../redux/api";
import { treeActions } from "../../../redux/slices/treeSlice";
import { workflowGroupsToRcTree } from "../../../utils/rctreeDataConversion";
import NewTemplateWizard from "../../ContextMenu/WorkflowGroupActions/NewTemplateWizard";
import { K, apiLabel } from "./apiServiceI18n";

const emptySelect = "";

function removeJsonExtension(templateName) {
  if (!templateName) return "";
  return templateName.endsWith(".json") ? templateName.slice(0, -5) : templateName;
}

/** Template names for a workflow group from get_*_templates_tree response (same as workbench Translate Data). */
function templateNamesForGroup(apiPayload, screen, workflowGroupName) {
  if (!workflowGroupName || apiPayload?.current_state !== "SUCCESS") return [];
  const tree = workflowGroupsToRcTree(apiPayload.data, screen);
  const groupNode = tree.find((n) => n.type === "workflowGroup" && n.title === workflowGroupName);
  if (!groupNode?.children?.length) return [];

  const templateType = screen === "inbound" ? "inboundTemplate" : "outboundTemplate";
  return groupNode.children
    .filter((c) => c.type === templateType)
    .map((c) => removeJsonExtension(c.title))
    .filter(Boolean);
}

/**
 * Wizard Step 3 — Translate Data (workflow group + inbound/outbound template pickers).
 */
export default function TranslateDataStep({ account, value, onChange, errors = {} }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentScreen = useSelector((s) => s.treeData.currentScreen);
  const prevScreenRef = useRef(currentScreen);
  const [ntwOpen, setNtwOpen] = useState(null);
  const contextAction = useSelector((s) =>
    ntwOpen ? s.treeData[ntwOpen]?.leftTree?.contextAction : null
  );

  const workflowsQuery = useGetWorkflowsCopyQuery({ account }, { skip: !account });
  const inboundListQuery = useGetCopyInboundTemplateListQuery({ account }, { skip: !account });
  const outboundListQuery = useGetCopyOutboundTemplateListQuery({ account }, { skip: !account });

  const workflowGroupOptions = useMemo(() => {
    const d = workflowsQuery.data;
    if (d?.current_state === "SUCCESS" && Array.isArray(d?.data?.workflow_groups)) {
      return d.data.workflow_groups.map((g) => g?.name).filter(Boolean);
    }
    return [];
  }, [workflowsQuery.data]);

  const workflowGroup = value.workflowGroup || emptySelect;

  const inboundOptions = useMemo(
    () => templateNamesForGroup(inboundListQuery.data, "inbound", workflowGroup),
    [inboundListQuery.data, workflowGroup]
  );

  const outboundOptions = useMemo(
    () => templateNamesForGroup(outboundListQuery.data, "outbound", workflowGroup),
    [outboundListQuery.data, workflowGroup]
  );

  const workflowsLoading = workflowsQuery.isLoading || workflowsQuery.isFetching;
  const inboundLoading = inboundListQuery.isLoading || inboundListQuery.isFetching;
  const outboundLoading = outboundListQuery.isLoading || outboundListQuery.isFetching;

  const patch = (fields) => onChange({ ...value, ...fields });

  const seedLeftTreeForNtw = (direction, wfg) => {
    dispatch(
      treeActions.setLeftTreeSelectedNode({
        screen: direction,
        type: "body",
        data: {
          title: wfg,
          workflowGroup: wfg,
          account,
          type: "workflowGroup",
          key: `wfg-ntw-${wfg}`,
        },
      })
    );
    dispatch(treeActions.setCurrentScreen(direction));
  };

  const openNewTemplate = (direction) => {
    if (!workflowGroup || workflowGroup === emptySelect) return;
    prevScreenRef.current = currentScreen;
    dispatch(treeActions.setLeftTreeContextActionValue({ screen: direction, data: false }));
    seedLeftTreeForNtw(direction, workflowGroup);
    setNtwOpen(direction);
  };

  const closeNewTemplate = () => {
    const direction = ntwOpen;
    setNtwOpen(null);
    dispatch(treeActions.setCurrentScreen(prevScreenRef.current || "workflow"));
    if (direction) {
      dispatch(treeActions.setLeftTreeContextActionValue({ screen: direction, data: false }));
    }
    inboundListQuery.refetch();
    outboundListQuery.refetch();
  };

  useEffect(() => {
    if (!ntwOpen || !contextAction?.name || contextAction.workflowGroup !== workflowGroup) {
      return undefined;
    }

    const direction = ntwOpen;
    const field = direction === "inbound" ? "inboundTemplate" : "outboundTemplate";
    const templateName = removeJsonExtension(contextAction.name);
    const listQuery = direction === "inbound" ? inboundListQuery : outboundListQuery;
    let cancelled = false;

    (async () => {
      const result = await listQuery.refetch();
      if (cancelled) return;

      const options = templateNamesForGroup(result.data, direction, workflowGroup);
      const selected =
        options.find((name) => name === templateName) ||
        options.find((name) => name.toLowerCase() === templateName.toLowerCase()) ||
        templateName;

      patch({ [field]: selected });
      setNtwOpen(null);
      dispatch(treeActions.setCurrentScreen(prevScreenRef.current || "workflow"));
      dispatch(treeActions.setLeftTreeContextActionValue({ screen: direction, data: false }));
    })();

    return () => {
      cancelled = true;
    };
  }, [contextAction, ntwOpen, workflowGroup]);

  return (
    <Stack spacing={3} sx={{ maxWidth: 900 }}>
      <Typography variant="subtitle1" fontWeight={700}>
        {t(K.API_SVC_WIZARD_BREADCRUMB_TRANSLATE)}
      </Typography>

      <FormControl fullWidth size="small" error={Boolean(errors.workflowGroup)}>
        <InputLabel id="translate-wfg-label">{apiLabel(K.WORKFLOW_GROUP)}</InputLabel>
        <Select
          labelId="translate-wfg-label"
          label={apiLabel(K.WORKFLOW_GROUP)}
          value={workflowGroup}
          disabled={workflowsLoading}
          onChange={(e) =>
            patch({
              workflowGroup: e.target.value,
              inboundTemplate: emptySelect,
              outboundTemplate: emptySelect,
            })
          }
        >
          <MenuItem value={emptySelect}>
            <em>{t(K.API_SVC_SELECT_WF_GROUP_OPTION)}</em>
          </MenuItem>
          {workflowGroupOptions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </Select>
        {workflowsLoading ? (
          <FormHelperText sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={14} /> {t(K.API_SVC_LOADING_WF_GROUPS)}
          </FormHelperText>
        ) : null}
        {errors.workflowGroup ? (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {errors.workflowGroup}
          </Typography>
        ) : null}
      </FormControl>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <FormControl fullWidth size="small" error={Boolean(errors.inboundTemplate)}>
              <InputLabel id="translate-ib-label">{t(K.INBOUND_TEMPLATE)}</InputLabel>
              <Select
                labelId="translate-ib-label"
                label={t(K.INBOUND_TEMPLATE)}
                value={value.inboundTemplate || emptySelect}
                disabled={
                  !workflowGroup ||
                  workflowGroup === emptySelect ||
                  inboundLoading
                }
                onChange={(e) => patch({ inboundTemplate: e.target.value })}
              >
                <MenuItem value={emptySelect}>
                  <em>{t(K.API_SVC_SELECT_INBOUND_OPTION)}</em>
                </MenuItem>
                {inboundOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title={t(K.BTN_CREATE_NEW_IN_TEM)} arrow>
              <IconButton
                size="small"
                sx={{ mt: 0.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                onClick={() => openNewTemplate("inbound")}
                disabled={!workflowGroup || workflowGroup === emptySelect}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {inboundLoading && workflowGroup && workflowGroup !== emptySelect ? (
            <FormHelperText sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} /> {t(K.API_SVC_LOADING_INBOUND)}
            </FormHelperText>
          ) : null}
          {errors.inboundTemplate ? (
            <Typography variant="caption" color="error">
              {errors.inboundTemplate}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <FormControl fullWidth size="small" error={Boolean(errors.outboundTemplate)}>
              <InputLabel id="translate-ob-label">{t(K.OUTBOUND_TEMPLATE)}</InputLabel>
              <Select
                labelId="translate-ob-label"
                label={t(K.OUTBOUND_TEMPLATE)}
                value={value.outboundTemplate || emptySelect}
                disabled={
                  !workflowGroup ||
                  workflowGroup === emptySelect ||
                  outboundLoading
                }
                onChange={(e) => patch({ outboundTemplate: e.target.value })}
              >
                <MenuItem value={emptySelect}>
                  <em>{t(K.API_SVC_SELECT_OUTBOUND_OPTION)}</em>
                </MenuItem>
                {outboundOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title={t(K.BTN_CREATE_NEW_OUT_TEM)} arrow>
              <IconButton
                size="small"
                sx={{ mt: 0.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                onClick={() => openNewTemplate("outbound")}
                disabled={!workflowGroup || workflowGroup === emptySelect}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {outboundLoading && workflowGroup && workflowGroup !== emptySelect ? (
            <FormHelperText sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} /> {t(K.API_SVC_LOADING_OUTBOUND)}
            </FormHelperText>
          ) : null}
          {errors.outboundTemplate ? (
            <Typography variant="caption" color="error">
              {errors.outboundTemplate}
            </Typography>
          ) : null}
        </Box>
      </Stack>

      {ntwOpen ? <NewTemplateWizard modalOpen toggle={closeNewTemplate} /> : null}
    </Stack>
  );
}
